/**
 * Iter-63.1 / Iter-63.1.1 — Prospect share engagement bridge.
 *
 * Emits non-PII engagement events to `/api/public/preview-engagement/event`
 * ONLY when the visitor arrived via a valid `?demo=<share_token>` prospect
 * share link. Never fires on real live traffic, never fires from `?preview=1`
 * admin previews without a share token, never fires from `sample-*` pages,
 * never carries consumer PII, never touches conversion pixels.
 *
 * Uses the browser-native `fetch` API (with keepalive) so this stays
 * fire-and-forget even when the tab is closing after a CTA tap.
 *
 * Iter-63.1.1 — privacy-safe unique-session counting. Each browser tab
 * gets ONE random UUID per prospect share token, kept in `sessionStorage`
 * so a page reload within the same tab does NOT count as a new session,
 * but a fresh tab / window DOES. The backend maintains a bounded set of
 * session ids per token so `engagement.unique_sessions` becomes a real
 * cross-tab pitch metric without fingerprinting.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND.replace(/\/$/, "")}/api`;

const ALLOWED = new Set([
  "page_view",
  "thank_you_view",
  "form_start",
  "form_complete_preview",
  "cta_primary_click",
  "cta_phone_click",
]);

const inflight = new Set();

// Iter-63.1.1 — sessionStorage-scoped preview-session ids per token.
// Never PII, never a fingerprint. Falls back to Math.random() only if
// `crypto.randomUUID` is unavailable AND the caller is a legacy browser.
function _newRandomId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  // Legacy fallback — RFC-4122ish but not cryptographically strong.
  return "pv-" + Math.random().toString(36).slice(2)
    + "-" + Date.now().toString(36);
}

export function getPreviewSessionId(token) {
  if (!token) return null;
  const key = `lf_preview_sid_${token}`;
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      const existing = window.sessionStorage.getItem(key);
      if (existing) return existing;
      const fresh = _newRandomId();
      window.sessionStorage.setItem(key, fresh);
      return fresh;
    }
  } catch { /* sessionStorage disabled — no session id, unique count won't inflate */ }
  return null;
}

/**
 * Fire a prospect engagement event.
 * Returns silently when any precondition fails — this is a fire-and-forget
 * analytics bridge, never a blocking user flow.
 *
 * @param {string} token - the share token (`?demo=<token>`)
 * @param {string} slug - the landing page slug
 * @param {string} event - one of ALLOWED event types
 * @param {object} opts - {isSamplePage?: boolean, dedupe?: string}
 */
export function emitPreviewEngagement(token, slug, event, opts = {}) {
  try {
    if (!token || !slug) return;
    if (!ALLOWED.has(event)) return;
    if (opts?.isSamplePage) return;  // sample pages never emit prospect events
    const key = opts.dedupe || `${token}:${slug}:${event}`;
    if (inflight.has(key)) return;
    inflight.add(key);
    if (typeof fetch !== "function") return;
    const sessionId = getPreviewSessionId(token);
    const body = { token, slug, event_type: event };
    if (sessionId) body.session_id = sessionId;
    fetch(`${API}/public/preview-engagement/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      keepalive: true,
      body: JSON.stringify(body),
    }).catch(() => { /* fire-and-forget */ });
  } catch { /* fire-and-forget */ }
}

export function isPreviewShareContext({ token, isSamplePage }) {
  return Boolean(token) && !isSamplePage;
}

export const _ALLOWED_EVENTS = ALLOWED;   // exported for tests
